/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionChoiceProps {
  id: string;
  label: string;
  value: string;
  questionId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionChoiceRecord extends Record<QuestionChoiceProps>({
  id: null as any,
  label: null as any,
  value: null as any,
  questionId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    label: string;
    value: string;
    questionId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionChoiceRecord {
    if (json == null) {
      throw new Error('QuestionChoiceRecord.fromJS: parameter cannot be null');
    }
    return new QuestionChoiceRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionChoiceJson.id',
      ),
      label: getFromOrThrow(json, (j) => j.label, 'QuestionChoiceJson.label'),
      value: getFromOrThrow(json, (j) => j.value, 'QuestionChoiceJson.value'),
      questionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'questionId', '00000000-0000-0000-0000-000000000000'),
        'QuestionChoiceJson.questionId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    label: string;
    value: string;
    questionId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionChoiceRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionChoiceRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionChoiceRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionChoiceJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      label: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.label, 'QuestionChoiceJson.label'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      value: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.value, 'QuestionChoiceJson.value'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'questionId', '00000000-0000-0000-0000-000000000000'),
            'QuestionChoiceJson.questionId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionChoiceProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionChoiceRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionChoiceViewModelBase implements QuestionChoiceProps {
  protected readonly _props: QuestionChoiceProps;
  private _json?: QuestionChoiceProps;

  get id(): string {
    return this._props.id;
  }
  get label(): string {
    return this._props.label;
  }
  get value(): string {
    return this._props.value;
  }
  get questionId(): string {
    return this._props.questionId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: QuestionChoiceProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        label: this._props.label,
        value: this._props.value,
        questionId: this._props.questionId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionChoiceEntities = (state: AppState) =>
  state.entityStores.questionChoice.entities;

export const selectQuestionChoiceById = (id: string) => (state: AppState) =>
  state.entityStores.questionChoice.entities.get(id);

export const selectQuestionChoiceMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionChoice.meta.get(id);

export const selectQuestionChoiceAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionChoiceById(id),
    selectQuestionChoiceMetadataById(id),
    (questionChoice, meta) => ({ questionChoice, meta }),
  );

export function selectQuestionChoiceEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionChoiceEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionChoiceRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionChoiceEntitiesByLabel(label: string) {
  return createSelector(selectAllQuestionChoiceEntities, (entities) =>
    entities.filter((e) => e.label === label).valueSeq(),
  );
}

export function selectQuestionChoiceEntitiesByValue(value: string) {
  return createSelector(selectAllQuestionChoiceEntities, (entities) =>
    entities.filter((e) => e.value === value).valueSeq(),
  );
}

export function selectQuestionChoiceEntitiesByQuestionId(questionId: string) {
  return createSelector(selectAllQuestionChoiceEntities, (entities) =>
    entities.filter((e) => e.questionId === questionId).valueSeq(),
  );
}
//#endregion Selectors
